import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';

/**
 * Keeps the browser tab in sync with the company branding held in Settings:
 * the favicon follows the uploaded logo, and the title follows the company
 * name. Renders nothing.
 *
 * Must live inside <Router> so it can tell the public site apart from the
 * admin panel.
 */

const escapeXml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Favicon drawn from the company's initial, for when no logo has been
 * uploaded. Keeps the tab on-brand instead of showing the browser's blank
 * page icon.
 */
const initialFavicon = (companyName) => {
  const letter = escapeXml((companyName || 'W').trim().charAt(0).toUpperCase() || 'W');
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">` +
    `<stop offset="0" stop-color="#22c1d6"/><stop offset="1" stop-color="#0e8ba0"/>` +
    `</linearGradient></defs>` +
    `<rect width="64" height="64" rx="14" fill="url(#g)"/>` +
    `<text x="32" y="44" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" ` +
    `font-size="34" font-weight="700" fill="#ffffff">${letter}</text>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/** Create the <link> if the document does not already have one. */
const setIconLink = (rel, href, type) => {
  let link = document.head.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  if (type) {
    link.setAttribute('type', type);
  } else {
    // An uploaded logo may be png/jpg/svg - let the browser sniff it
    link.removeAttribute('type');
  }
  link.setAttribute('href', href);
};

// Public pages carry their own search-engine title and description. The
// landing page's pair also sits statically in public/index.html, which is what
// crawlers read first; every other page follows the company name.
const PUBLIC_PAGES = {
  '/': {
    title: 'Book Hotel - Tour and Travel package on weotp',
    description:
      'WeOtp is an online hotel booking and travel platform that connects customers with hotels and travel services. ' +
      'We offer hotel bookings, tour packages, and tour & travel solutions at convenient prices. Our goal is to make ' +
      'travel simple, affordable, and hassle-free by providing customers with reliable options and a smooth booking experience.'
  },
  '/owner': {
    title: 'Partner login - Manage your listings on WeOtp',
    description: 'Log in with your registered phone number to manage your hotel, spa, tour and travel package listings on WeOtp.'
  },
  '/stays': { title: 'Hotels & stays - book on WeOtp', description: 'Find hotels, resorts and guest houses with room-wise prices, hourly stays and direct WhatsApp booking on WeOtp.' },
  '/spas': { title: 'Spas & wellness - book on WeOtp', description: 'Massages, therapies and spa day packages with prices and timings. Book directly on WhatsApp with WeOtp.' },
  '/tours': { title: 'Tours & activities - book on WeOtp', description: 'Sightseeing, treks and experiences with options, dates and time slots. Book directly on WhatsApp with WeOtp.' },
  '/packages': { title: 'Travel packages - book on WeOtp', description: 'Curated holiday packages with itineraries, inclusions and variants. Enquire directly on WhatsApp with WeOtp.' },
  '/list-your-property': {
    title: 'List Your Property on WeOtp - Hotels Apartments • Tour & Travel Packages etc',
    description:
      'List Your Property on WeOtp - List your Hotels, Apartments, Resorts, Spas, and Tour & Travel Packages on WeOtp ' +
      'and reach more customers directly. Get greater visibility, receive direct customer enquiries, and grow your ' +
      'business with an easy online listing. Join WeOtp today and take your property business to the next level.'
  }
};

const setMetaDescription = (content) => {
  let meta = document.head.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

const BrandHead = () => {
  const { companyName, logoUrl } = useSettings();
  const location = useLocation();

  const name = companyName || 'We OTP';
  const isAdminArea = location.pathname === '/sy-admin' || location.pathname.startsWith('/sy-admin/');
  const publicPage = PUBLIC_PAGES[location.pathname.replace(/\/+$/, '') || '/'];

  useEffect(() => {
    if (publicPage) {
      document.title = publicPage.title;
      setMetaDescription(publicPage.description);
    } else {
      document.title = isAdminArea ? `${name} · Admin` : name;
      setMetaDescription(PUBLIC_PAGES['/'].description);
    }
  }, [name, isAdminArea, publicPage]);

  useEffect(() => {
    const href = logoUrl || initialFavicon(name);
    const type = logoUrl ? undefined : 'image/svg+xml';

    setIconLink('icon', href, type);
    setIconLink('apple-touch-icon', href);
  }, [logoUrl, name]);

  return null;
};

export default BrandHead;
